import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import {
    BOARD_SHARE_LINK_ACCESS_PASSWORD_GET_V2,
    BOARD_SHARE_LINK_ACCESS_PASSWORD_SET_V2,
} from "@/api/apiEndpoints";
import { canv } from "@/canv/global";
import { MakeRequest } from "@/canv/utils";
import { SNACKBAR_TIME } from "@/elements/text/constants";
import { Check, CloseIcon, Lock, Trash } from "@/icons";
import { PASSWORD_REG } from "@/interface/ShareWindow/constants";
import { Button } from "@/shared/ui/Button";
import { showSnackbar } from "@/snackbar/snackbar";
import styles from "../ShareWindow.module.scss";

interface FormValues {
    password: string;
}

export const ChangePassword = () => {
    const { t } = useTranslation();
    const [isVisibleForm, setIsVisibleForm] = useState(false);
    const [password, setPassword] = useState("");

    const getBoardPassword = useCallback(async () => {
        const result = await MakeRequest(BOARD_SHARE_LINK_ACCESS_PASSWORD_GET_V2, {
            token: canv.getInitDataProp("userToken"),
            boardUID: canv.getInitDataProp("boardUID"),
        });
        if (result?.password !== undefined) {
            setPassword(result.password);
        }
    }, []);

    const schema = yup.object({
        password: yup
            .string()
            .required(t("share.passwordEmpty"))
            .max(50, t("share.passwordMax"))
            .matches(PASSWORD_REG, t("share.passwordPattern"))
            .min(4, t("share.passwordMin")),
    });

    const {
        control,
        setValue,
        watch,
        trigger,
        clearErrors,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: {
            password: "",
        },
    });

    const pass = watch("password");
    const isSet = password !== "";

    const handleCheck = async () => {
        const isValid = await trigger("password");
        if (!isValid) return;

        try {
            await MakeRequest(BOARD_SHARE_LINK_ACCESS_PASSWORD_SET_V2, {
                token: canv.getInitDataProp("userToken"),
                boardUID: canv.getInitDataProp("boardUID"),
                password: pass,
            });
            await getBoardPassword();
            showSnackbar(t("share.notifyPasswordChanged"), {
                type: "success",
                duration: SNACKBAR_TIME,
            });
        } finally {
            setValue<"password">("password", "");
            setIsVisibleForm(false);
        }
    };

    const handleCloseForm = () => {
        setIsVisibleForm(false);
        setValue<"password">("password", "");
        clearErrors("password");
    };

    const handleDeletePassword = async () => {
        await MakeRequest(BOARD_SHARE_LINK_ACCESS_PASSWORD_SET_V2, {
            token: canv.getInitDataProp("userToken"),
            boardUID: canv.getInitDataProp("boardUID"),
            password: "",
        });
        await getBoardPassword();

        showSnackbar(t("share.notifyPasswordDeleted"), {
            type: "success",
            duration: SNACKBAR_TIME,
        });
        setIsVisibleForm(false);
    };

    useEffect(() => {
        getBoardPassword();
    }, [getBoardPassword]);

    return (
        <div className={styles.pass}>
            {!isVisibleForm ? (
                <div className={styles.buttonsWrapper}>
                    <Button
                        className={styles.lockButton}
                        onClick={() => setIsVisibleForm(true)}
                        variant="tertriary"
                    >
                        <>
                            <div className={!isSet ? styles.lockWrapper : styles.invisible}>
                                <Lock /> <span>{t("share.setPassword")}</span>
                            </div>
                            <div className={isSet ? "" : styles.invisible}>
                                {t("share.changePassword")}
                            </div>
                        </>
                    </Button>
                    {isSet && (
                        <Button
                            className={styles.delete}
                            variant="tertriary"
                            onClick={handleDeletePassword}
                        >
                            <Trash />
                        </Button>
                    )}
                </div>
            ) : (
                <div className={styles.passForm}>
                    <input type="text" autoComplete="username" style={{ display: "none" }} />
                    <Controller
                        name={"password"}
                        control={control}
                        render={({ field: { onChange, value, ref } }) => (
                            <input
                                ref={ref}
                                autoComplete="new-password"
                                className={styles.passwordFieldInput}
                                type="password"
                                placeholder=""
                                value={value}
                                onChange={onChange}
                                onFocus={() => {
                                    canv.appInterface.inputFocused = 1;
                                }}
                                onBlur={() => {
                                    canv.appInterface.inputFocused = 0;
                                }}
                            />
                        )}
                    />
                    {errors?.password?.message && (
                        <div className={styles.passError}>{errors.password.message}</div>
                    )}
                    <button type={"button"} className={styles.button} onClick={handleCheck}>
                        <Check />
                    </button>
                    <button type={"button"} className={styles.button} onClick={handleCloseForm}>
                        <CloseIcon />
                    </button>
                </div>
            )}
        </div>
    );
};
